import type { ReactNode } from 'react';

/**
 * The page header every screen in the design opens with: an uppercase
 * breadcrumb kicker, an 800-weight title, optional actions on the right, and a
 * 2px ink rule beneath.
 *
 * Extracted because six screens repeat it verbatim, and a rule that is 2px on
 * five pages and 1px on the sixth is exactly the drift a shared component
 * prevents.
 */
export interface PageHeaderProps {
  /** e.g. "Sales · Lead CRM". Rendered uppercase. */
  kicker: string;
  title: string;
  /** Optional supporting line under the title. */
  description?: string;
  actions?: ReactNode;
}

export function PageHeader({ kicker, title, description, actions }: PageHeaderProps): JSX.Element {
  return (
    <>
      <div className="flex flex-wrap items-end gap-[18px]">
        <div>
          <div className="mb-[9px] text-[11.5px] font-medium uppercase leading-none tracking-caps text-neutral-500">
            {kicker}
          </div>
          <h1 className="text-[27px] font-heading leading-[1.1] tracking-tight">{title}</h1>
          {description !== undefined && (
            <p className="mt-[7px] text-[13.5px] leading-[1.4] text-neutral-600">{description}</p>
          )}
        </div>
        {actions !== undefined && (
          <div className="ml-auto flex flex-wrap items-center gap-2.5">{actions}</div>
        )}
      </div>
      <div className="my-5 mb-[18px] h-0.5 bg-ink" />
    </>
  );
}

/** Wrapper matching the design's screen padding + entry animation. */
export function PageBody({ children }: { children: ReactNode }): JSX.Element {
  return <div className="animate-gy-in px-5 pb-[46px] pt-6 md:px-8">{children}</div>;
}
